package fr.frux.shared.domain.model

import fr.frux.shared.content.ContentModerationPayload

/**
 * Une notification in-app (#225 P0, ADR 0017). Domaine pur, aucune dépendance DTO (#109).
 *
 * [payload] reste transporté tel quel (libellés déjà dénormalisés côté serveur au moment de
 * l'écriture) — le domaine ne réinterprète pas les clés, seule l'UI sait lesquelles afficher par
 * [kind].
 */
data class Notification(
    val id: Long,
    val kind: NotificationKind,
    val subjectType: NotificationSubjectType,
    val subjectId: Long,
    val actorUserId: Long? = null,
    val payload: Map<String, String> = emptyMap(),
    val createdAt: Long,
    val readAt: Long? = null,
) {
    val isUnread: Boolean get() = readAt == null
}

/**
 * Miroir domaine de `fr.frux.shared.dto.NotificationKindDto` (#109). [UNKNOWN] en dernier membre
 * (#253/#157, résilience aux kinds futurs) : jamais montré à l'utilisateur, filtré en amont par
 * `NotificationListResponse.toDomain`. Présent ici seulement pour que la traduction DTO→domaine
 * reste une fonction totale.
 */
enum class NotificationKind {
    HARVEST_JOINED,
    HARVEST_CANCELLED,
    HARVEST_STARTED,
    TREE_DISPUTED,
    REPORT_RESOLVED,
    MATURITY_DIGEST,
    SESSION_REVOKED,

    // back-office lot 1 (#333), in-app seulement
    SPECIES_SUGGESTION_ACCEPTED,
    SPECIES_SUGGESTION_DISMISSED,

    // back-office lot 2 (#340), in-app seulement, jamais le motif
    TREE_SPECIES_CORRECTED,

    // modération de contenu (#550), in-app seulement
    CONTENT_MODERATED,

    // récoltes (#581, #582, #583)
    HARVEST_LEFT,
    HARVEST_FULL,
    HARVEST_COMPLETED,

    // retour utilisateur traité (#586), in-app seulement
    FEEDBACK_HANDLED,

    // arbres (#584, #585), une ligne par arbre et par jour
    TREE_ACTIVITY,
    FAVORITE_TREE_CHANGED,

    // rappel de récolte dans les 24 h (#580)
    HARVEST_REMINDER,

    // fin d'abonnement Frux+ (#587)
    PREMIUM_EXPIRING,
    PREMIUM_EXPIRED,
    UNKNOWN,
}

/** Miroir domaine de `fr.frux.shared.dto.NotificationSubjectTypeDto` (#109). [UNKNOWN] : même rôle
 *  de repli que [NotificationKind.UNKNOWN]. */
enum class NotificationSubjectType {
    HARVEST,
    TREE,
    DIGEST,
    ACCOUNT,

    // back-office lot 1 (#333), in-app seulement
    SPECIES_SUGGESTION,

    // retour utilisateur (#586) et compte signalé (#588), in-app seulement
    FEEDBACK,
    USER_REPORT,
    UNKNOWN,
}

/**
 * #338 : une ligne de notification a soit une destination, soit aucune affordance de clic — jamais
 * une carte cliquable qui ne mène nulle part. [ACCOUNT] (session révoquée, #295, pas d'écran de
 * sessions, ADR 0019), [SPECIES_SUGGESTION] (#333), [FEEDBACK] (#586) et [USER_REPORT] (#588), dont
 * le texte porte déjà toute l'information utile, et [UNKNOWN] (résilience aux kinds futurs)
 * n'ouvrent rien côté client — cf. `FruxEntryProvider`.
 */
val NotificationSubjectType.hasDestination: Boolean
    get() =
        when (this) {
            NotificationSubjectType.HARVEST,
            NotificationSubjectType.TREE,
            NotificationSubjectType.DIGEST,
            -> true

            NotificationSubjectType.ACCOUNT,
            NotificationSubjectType.SPECIES_SUGGESTION,
            NotificationSubjectType.FEEDBACK,
            NotificationSubjectType.USER_REPORT,
            NotificationSubjectType.UNKNOWN,
            -> false
        }

/**
 * Destination d'une notification précise (#550) : [NotificationSubjectType.hasDestination], sauf
 * l'arbre masqué par la modération. Il rend 404 sur toutes les lectures publiques, la ligne
 * n'ouvre donc rien plutôt qu'une fiche introuvable.
 */
val Notification.hasDestination: Boolean
    get() =
        if (kind == NotificationKind.CONTENT_MODERATED &&
            payload[ContentModerationPayload.KEY_ACTION] == ContentModerationPayload.ACTION_TREE_HIDDEN
        ) {
            false
        } else {
            subjectType.hasDestination
        }

/**
 * Page de notifications (#225 P0), même patron que [NearbyHarvests] : envelope avec le total côté
 * source de vérité ([unreadCount]) plutôt qu'un recalcul client.
 */
data class NotificationPage(
    val notifications: List<Notification>,
    val unreadCount: Int,
    val nextCursor: Long? = null,
)

/**
 * Préférences de notification de l'utilisateur (#225 P0, ADR 0017 D9). [inAppEnabled] est toujours
 * `true` — non modifiable, rendu pour la complétude de la lecture (cf. `NotificationsApi`).
 */
data class NotificationPreferences(
    val inAppEnabled: Boolean = true,
    val emailEnabled: Boolean = false,
    val pushEnabled: Boolean = false,
    val harvestEvents: Boolean = true,
    val treeEvents: Boolean = true,
    val maturityAlerts: Boolean = true,
    /** Digest de maturité réservé à Frux+ et compte non abonné (#455) : l'interrupteur est inactif. */
    val maturityAlertsLocked: Boolean = false,
)
